import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import QRCode from 'qrcode.react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import qrIcon from '../QRButton/qrcode.svg';
import SvgIcon from '../SvgIcon/SvgIcon';
import Tooltip from '../Tooltip/Tooltip';
import Button, { ButtonType } from '../Button/Button';
import MessageBlock, { MessageType, BlockStyle } from '../MessageBlock/MessageBlock';
import TestIds from '../../tests/TestIds';

import styles from './QRViewButton.module.scss';

const messages = defineMessages({
	showQR: {
		id: 'payments.show_qr',
		defaultMessage: 'Show QR code',
	},
	x_QRCode: {
		id: 'payments.x_QRCode',
		defaultMessage: '{Label} QR code',
	},
	copy_x: {
		id: 'payments.copy_x',
		defaultMessage: 'Copy {Label}',
	},
});

type Props = {
	className?: string;
	label: string;
	data: string;
	displayValue?: string;
	infoMessage?: string;
};

const QRViewButton: FC<Props> = ({ className, data, displayValue, label, infoMessage }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<div className={classNames(styles.container, styles.flex, className)}>
			<Tooltip content={formatMessage(messages.showQR)}>
				<div
					data-cy={TestIds.ShowQR}
					onClick={() => setIsOpen(true)}
					className={styles.flex}
				>
					<SvgIcon className={classNames(styles.icon, styles.flex)} src={qrIcon} />
				</div>
			</Tooltip>
			{isOpen && (
				<>
					<div className={styles.overlay} onClick={() => setIsOpen(false)} />
					<div className={styles.qrView} data-cy={TestIds.QRView}>
						<div className={styles.heading}>
							<span>{formatMessage(messages.x_QRCode, { Label: label })}</span>
							<FontAwesomeIcon
								className={styles.closeIcon}
								icon={faXmark}
								onClick={() => setIsOpen(false)}
							/>
						</div>
						<div className={styles.content}>
							<div className={styles.qrWrapper}>
								<QRCode data-cy={TestIds.QR} value={data} size={170} />
							</div>
							<div className={styles.info}>
								<label>{label}</label>
								<span className={styles.value}>{displayValue}</span>
							</div>
							{infoMessage && (
								<MessageBlock
									data-cy={TestIds.QRInfoMessage}
									type={MessageType.Info}
									style={BlockStyle.Fill}
									message={infoMessage}
									withIcon
								/>
							)}
							<Button
								isFullWidth
								data-cy={TestIds.CopyQR}
								text={formatMessage(messages.copy_x, { Label: label })}
								type={ButtonType.COPY}
								dataToCopy={displayValue || data}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default QRViewButton;
